import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'

import competencyOptions from 'modules/competencies/store/collections/competencyOptions'

interface ICompetency {
  [key: string]: any
}

export class Competency extends Model<ICompetency> {
  static urlRoot() {
    return '/competencies/competencies'
  }

  static STATUS = {
    ACTIVE: 'active',
    ARCHIVED: 'archived',
  }

  constructor(data: ICompetency) {
    super(data)

    makeObservable(this, {
      isUsed: computed,
      name: computed,
      archived: computed,
      options: computed,
      optionsWithDescription: computed,
      range: computed,
      children: computed,
      parent: computed,
      id: computed,
    })
  }

  get id(): number {
    return Number(this.attributes.id)
  }

  get name(): string {
    return this.attributes.name
  }

  get archived(): boolean {
    return this.attributes.archived
  }

  get options() {
    return competencyOptions
      .filter({ competency_id: this.attributes.id })
      .sort((a, b) => a.value - b.value)
  }

  get optionsWithDescription() {
    return this.options.filter((option) => option.attributes.description)
  }

  get range(): [number, number] | null {
    const ranges = this.options.sort((a, b) => a.value - b.value)
    const min = ranges[0]?.value
    const max = ranges[ranges.length - 1]?.value

    if (min === undefined || max === undefined) return null

    return [min, max]
  }

  get children(): Competency[] {
    return this.collection?.filter({ parent_id: this.attributes.id }) ?? []
  }

  archiveCompetency = async (archived: boolean) => {
    await this.rpc('archive', { archived })
    await this.collection?.fetch() // We do a fetch because the archvie action is propagated between all the children
  }

  get parent(): Competency | null {
    if (!this.attributes.parent_id) return null

    const parent = this.collection?.get(this.attributes.parent_id)

    if (!parent) throw new Error(`Parent with id ${this.attributes.parent_id}} not found`)

    return parent
  }

  get isUsed(): boolean {
    return this.attributes.used
  }

  async destroyWithNotifications() {
    const destroy = await this.rpc('destroy_with_notifications')
    await this.collection?.fetch()

    return destroy
  }
}
